import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
    buildLiquidacionHonorariosView,
    downloadLiquidacionHonorariosPdf,
    getLiquidacionHonorariosStyles,
    printLiquidacionHonorarios,
} from "./liquidacionProfesionalPrint";
import { resolverProveedorDesdeLiquidacion } from "../../../utils/liquidacionHonorariosFacturacion";

export default function ModalLiquidacionHonorarios({
    visible,
    data,
    client,
    onClose,
    onGenerarEgreso,
    onGenerarDocumentoSoporte,
    generandoDoc: generandoDocProp,
}) {
    const history = useHistory();
    const [generandoDocLocal, setGenerandoDocLocal] = useState(false);
    const generandoDoc = generandoDocProp != null ? generandoDocProp : generandoDocLocal;

    if (!visible || !data) return null;

    const view = buildLiquidacionHonorariosView(data, client);
    const puedeGenerar = data.estado !== "anulada";

    const handlePrint = () => printLiquidacionHonorarios(view);

    const handleDownloadPdf = () => {
        try {
            downloadLiquidacionHonorariosPdf(data, client);
        } catch (e) {
            console.error(e);
            alert("No fue posible generar el PDF.");
        }
    };

    const navegarConProveedor = async (destino) => {
        if (!puedeGenerar) {
            alert("No se puede generar documento desde una liquidación anulada.");
            return;
        }
        setGenerandoDocLocal(true);
        try {
            const pref = await resolverProveedorDesdeLiquidacion(data);
            if (typeof onClose === "function") onClose();
            history.push({
                pathname: destino,
                state: { fromLiquidacion: true, ...pref },
            });
        } catch (err) {
            console.error(err);
            const msgErr =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                "No se pudo preparar el proveedor.";
            alert(msgErr);
        } finally {
            setGenerandoDocLocal(false);
        }
    };

    const handleGenerarEgreso = () => {
        if (typeof onGenerarEgreso === "function") {
            onGenerarEgreso();
            return;
        }
        navegarConProveedor("/crear-egreso");
    };

    const handleGenerarDocumentoSoporte = () => {
        if (typeof onGenerarDocumentoSoporte === "function") {
            onGenerarDocumentoSoporte();
            return;
        }
        navegarConProveedor("/crear-documento-soporte");
    };

    return (
        <>
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ background: "rgba(15, 23, 42, 0.5)" }}
                onClick={onClose}
            >
                <div
                    className="modal-dialog modal-dialog-centered modal-xl"
                    role="document"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content" style={{ borderRadius: "12px", overflow: "hidden" }}>
                        <div className="modal-header py-2 border-bottom">
                            <h5 className="modal-title mb-0">Vista Previa</h5>
                            <button type="button" className="close" onClick={onClose} aria-label="Cerrar">
                                <span>&times;</span>
                            </button>
                        </div>
                        <div
                            className="modal-body p-0"
                            style={{ maxHeight: "78vh", overflowY: "auto", background: "#f8fafc" }}
                        >
                            <LiquidacionHonorariosBody view={view} />
                        </div>
                        <div className="modal-footer lh-modal-footer py-2 border-top d-flex flex-wrap align-items-center justify-content-end">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={generandoDoc}>
                                Cerrar
                            </button>
                            {puedeGenerar && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-dark btn-sm"
                                        onClick={handleGenerarEgreso}
                                        disabled={generandoDoc}
                                    >
                                        <i className="fas fa-money-bill-wave mr-1"></i>
                                        {generandoDoc ? "Preparando…" : "Crear Egreso"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-dark btn-sm"
                                        onClick={handleGenerarDocumentoSoporte}
                                        disabled={generandoDoc}
                                    >
                                        <i className="fas fa-file-invoice mr-1"></i>
                                        {generandoDoc ? "Preparando…" : "Crear Documento Soporte"}
                                    </button>
                                </>
                            )}
                            <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleDownloadPdf} disabled={generandoDoc}>
                                <i className="fas fa-file-pdf mr-1"></i> Descargar PDF
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handlePrint} disabled={generandoDoc}>
                                <i className="fas fa-print mr-1"></i> Imprimir
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <LiquidacionHonorariosStyles />
        </>
    );
}

function LiquidacionHonorariosBody({ view }) {
    const {
        esAnulada,
        fechaEmision,
        profesional,
        periodo,
        clinicaVisible,
        nombreClinica,
        pct,
        facturasCount,
        fechaAnulacion,
        motivoAnulacion,
        valorTotal,
        valorBruto,
        valorLiquidacion,
        ajusteVal,
        filasFacturas,
    } = view;

    return (
        <div className="lh-document">
            <div className="lh-report-title">
                LIQUIDACIÓN DE HONORARIOS{esAnulada ? " — ANULADA" : ""}
            </div>
            <div className="lh-fecha-emision">Fecha de emisión: {fechaEmision}</div>

            {esAnulada && (
                <div className="lh-anulada-banner">LIQUIDACIÓN ANULADA — Solo referencia histórica</div>
            )}

            <div className="lh-info-card">
                <div className="lh-info-grid">
                    <div className="lh-info-col">
                        <p><strong>Profesional:</strong> {profesional}</p>
                        <p><strong>Período:</strong> {periodo}</p>
                        {clinicaVisible && <p><strong>Clínica:</strong> {nombreClinica}</p>}
                    </div>
                    <div className="lh-info-col">
                        <p><strong>Porcentaje pactado:</strong> {pct}%</p>
                        <p><strong>Facturas incluidas:</strong> {facturasCount}</p>
                        {esAnulada && fechaAnulacion && (
                            <p><strong>Fecha anulación:</strong> {fechaAnulacion}</p>
                        )}
                        {esAnulada && motivoAnulacion && (
                            <p><strong>Motivo anulación:</strong> {motivoAnulacion}</p>
                        )}
                    </div>
                </div>
            </div>

            <div className="lh-saldo-box">
                <div className="lh-saldo-box__header">TOTAL A PAGAR</div>
                <div className="lh-saldo-box__value">{valorTotal}</div>
            </div>

            <div className="lh-section-title">FACTURAS DEL PERÍODO</div>
            <div className="lh-table-wrap">
                <table className="lh-table">
                    <thead>
                        <tr>
                            <th className="text-center">N° Factura</th>
                            <th className="text-center">Tipo</th>
                            <th>Paciente</th>
                            <th className="text-center">Fecha</th>
                            <th className="text-right">Valor bruto</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filasFacturas.length ? filasFacturas.map((f) => (
                            <tr key={f.key}>
                                <td className="text-center">{f.numero}</td>
                                <td className="text-center">{f.tipo}</td>
                                <td>{f.paciente}</td>
                                <td className="text-center">{f.fecha}</td>
                                <td className="text-right">{f.valor}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={5} className="text-center lh-empty">Sin facturas en el periodo</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="lh-section-title">RESUMEN DE LIQUIDACIÓN</div>
            <div className="lh-resumen-card">
                <table className="lh-resumen">
                    <tbody>
                        <tr>
                            <td className="label">Valor bruto facturado</td>
                            <td className="value">{valorBruto}</td>
                        </tr>
                        <tr>
                            <td className="label">Liquidación base ({pct}%)</td>
                            <td className="value">{valorLiquidacion}</td>
                        </tr>
                        <tr>
                            <td className="label">Ajuste</td>
                            <td className="value">{ajusteVal}</td>
                        </tr>
                        <tr className="total-final">
                            <td className="label">TOTAL A PAGAR</td>
                            <td className="value">{valorTotal}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function LiquidacionHonorariosStyles() {
    return (
        <style>{`
            ${getLiquidacionHonorariosStyles()}
            .lh-modal-footer {
                gap: 4px !important;
            }
            .lh-modal-footer > .btn {
                margin: 0 !important;
            }
        `}</style>
    );
}
